import React, { useEffect, useMemo, useState } from "react";
import NavBar from "../../components/common/NavBar";
import { useMutation, useQuery } from "@apollo/client";
import PaginatedFlatTable from "../../components/pagination/PaginatedFlatTable";
import { toast } from "react-toastify";
import FeeCalculator from "../../components/stripe/FeeCalculator";
import { GlobalStyles } from "../../constants/styles";
import useThemeColors from "../../components/organization/portal/useThemeColors";
import { alphaFromHex } from "../../util/colors";
import { MY_STRIPE_ACCOUNTS, UPDATE_STRIPE_ACCOUNT } from "../../components/stripe/mutations";
import CreateStripeAccountModal from "../../components/stripe/CreateStripeAccountModal";
import useTranslation from "../../i18n/useTranslation";
import { getLocalizedGraphQLError } from "../../util/graphqlError";

export default function StripeAccountsPage() {
    const scopeId = "kbola-stripe-accounts-page";
    const colors = useThemeColors();
    const { t } = useTranslation();

    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [accounts, setAccounts] = useState([]);
    const [showModal, setShowModal] = useState(false);

    useEffect(() => {
        if (!showModal) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prev || "";
        };
    }, [showModal]);

    const { loading, error, refetch } = useQuery(MY_STRIPE_ACCOUNTS, {
        fetchPolicy: "cache-and-network",
        onCompleted: (data) => {
            setAccounts(data?.stripeAccounts || []);
            setTotalPages(1);
        },
    });

    const [generateLink] = useMutation(UPDATE_STRIPE_ACCOUNT);

    const navBarElem = {
        pageTitle: t("stripeAccountsPage.pageTitle"),
        links: [
            { pageTitle: t("stripeAccountsPage.payments"), navLink: null, active: false },
            { pageTitle: t("stripeAccountsPage.stripeAccounts"), navLink: null, active: true },
        ],
    };

    const handleCompleteVerification = async (accountId) => {
        try {
            const { data } = await generateLink({ variables: { accountId } });
            if (data?.generateStripeOnboardingLink?.url) {
                window.open(data.generateStripeOnboardingLink.url, "_blank");
            } else {
                toast.warning(t("stripeAccountsPage.onboardingLinkFailed"));
            }
        } catch (err) {
            console.error(err);
            toast.error(t("stripeAccountsPage.onboardingLinkError"));
        }
    };

    const [liveCalc, setLiveCalc] = useState({
        currency: "USD",
        amountPaid: 5.0,
        appFeePct: 5,
        stripePct: 2.9,
        stripeFixed: 0.3,
        appFee: 0.25,
        toLeagueBeforeStripe: 4.75,
        stripeFee: 0.44,
        leagueNet: 4.31,
    });

    const fmt = useMemo(
        () =>
            new Intl.NumberFormat(undefined, {
                style: "currency",
                currency: liveCalc.currency || "USD",
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }),
        [liveCalc.currency]
    );

    const ui = useMemo(() => {
        return {
            wrap: {
                borderRadius: 18,
                overflow: "hidden",
                border: "1px solid rgba(0,0,0,0.08)",
                background: "#fff",
                boxShadow: "0 18px 44px rgba(0,0,0,0.10)",
            },

            header: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                padding: "12px 14px",
                background: "linear-gradient(135deg, rgba(var(--kbs-primary-rgb, 0,173,255), 0.18), rgba(0,0,0,0))",
                borderBottom: "1px solid rgba(0,0,0,0.06)",
            },
            titleWrap: { display: "flex", alignItems: "center", gap: 10, minWidth: 0 },
            badge: {
                fontSize: 12,
                fontWeight: 900,
                letterSpacing: "0.03em",
                padding: "6px 10px",
                borderRadius: 999,
                background: colors.primary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: colors.textOnPrimary,
                whiteSpace: "nowrap",
            },
            title: {
                margin: 0,
                fontSize: 14,
                fontWeight: 900,
                color: colors.textOnPrimary,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
            },
            sub: { margin: 0, fontSize: 12.5, fontWeight: 700, color: colors.textOnPrimary },
            headerRight: { display: "flex", alignItems: "center", gap: 10 },

            btn: {
                borderRadius: 12,
                padding: "10px 12px",
                fontWeight: 900,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                border: "1px solid rgba(0,0,0,0.12)",
                background: "#fff",
                color: "#14151a",
                cursor: "pointer",
                lineHeight: 1,
                textDecoration: "none",
            },
            btnPrimary: {
                background: GlobalStyles.colors.canary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: GlobalStyles.colors.woodsmoke,
            },

            body: { padding: 14 },

            infoCard: {
                borderRadius: 18,
                overflow: "hidden",
                border: "1px solid rgba(0,0,0,0.08)",
                background: "#fff",
                boxShadow: "0 18px 44px rgba(0,0,0,0.10)",
            },
            infoHeader: {
                padding: "12px 14px",
                background: `linear-gradient(
                    135deg,
                    ${alphaFromHex(colors.primary, 0.50)},
                    ${alphaFromHex(colors.primary, 0.00)}
                )`,
                borderBottom: "1px solid rgba(0,0,0,0.06)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
            },
            infoTitleWrap: { display: "flex", alignItems: "center", gap: 10, minWidth: 0 },
            infoBody: { padding: 14 },
            infoText: { margin: 0, color: "rgba(20,21,26,0.82)", fontWeight: 700, lineHeight: 1.45 },
            infoP: { margin: "10px 0 0 0", color: "rgba(20,21,26,0.74)", fontWeight: 700 },

            chip: {
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 10px",
                borderRadius: 999,
                border: "1px solid rgba(0,0,0,0.10)",
                background: "rgba(var(--kbs-primary-rgb, 0,173,255), 0.22)",
                color: "#14151a",
                fontWeight: 900,
                fontSize: 12,
                lineHeight: 1,
                whiteSpace: "nowrap",
            },
            chipSuccess: {
                borderColor: "rgba(25,135,84,0.22)",
                background: "rgba(25,135,84,0.10)",
                color: "#198754",
            },
            chipWarn: {
                borderColor: "rgba(255,193,7,0.35)",
                background: "rgba(255,193,7,0.14)",
                color: "#8a6d00",
            },

            verifyBtn: {
                borderRadius: 12,
                padding: "8px 10px",
                fontWeight: 900,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                border: "1px solid rgba(255,193,7,0.35)",
                background: "rgba(255,193,7,0.14)",
                color: "#8a6d00",
                cursor: "pointer",
                lineHeight: 1,
                whiteSpace: "nowrap",
            },

            modalBackdrop: { backgroundColor: "rgba(0,0,0,0.55)" },
            modalDialog: { maxWidth: 720 },
            modalContent: {
                borderRadius: 18,
                overflow: "hidden",
                border: "1px solid rgba(0,0,0,0.08)",
                boxShadow: "0 18px 44px rgba(0,0,0,0.18)",
            },
            modalHeader: {
                padding: "12px 14px",
                background: "linear-gradient(135deg, rgba(var(--kbs-primary-rgb, 0,173,255), 0.18), rgba(0,0,0,0))",
                borderBottom: "1px solid rgba(0,0,0,0.06)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                position: "sticky",
                top: 0,
                zIndex: 3,
            },
            modalTitle: { margin: 0, fontWeight: 900, fontSize: 14, color: "#14151a" },
            closeBtn: {
                width: 38,
                height: 38,
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.10)",
                background: "#fff",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                lineHeight: 1,
            },
            modalBody: {
                padding: 14,
                background: "#fff",
                maxHeight: "calc(100vh - 220px)",
                overflowY: "auto",
            },
        };
    }, [colors.primary, colors.textOnPrimary]);

    return (
        <div className="content-wrapper" id={scopeId}>
            <NavBar navBarElem={navBarElem} />

            <section className="content">
                {error && <div className="alert alert-danger col-6 mx-auto text-center">{getLocalizedGraphQLError(error, t, "errors.somethingWentWrong")}</div>}

                <div className="row mb-3">
                    <div className="col-12">
                        <div style={ui.infoCard}>
                            <div style={ui.infoHeader}>
                                <div style={ui.infoTitleWrap}>
                                    <span style={ui.badge}>{t("stripeAccountsPage.infoBadge")}</span>
                                    <div style={{ minWidth: 0 }}>
                                        <h3 style={ui.title}>{t("stripeAccountsPage.infoTitle")}</h3>
                                        <p style={ui.sub}>{t("stripeAccountsPage.infoSubtitle")}</p>
                                    </div>
                                </div>
                            </div>

                            <div style={ui.infoBody}>
                                <p style={ui.infoText}>
                                    {t("stripeAccountsPage.infoText1")}{" "}
                                    <strong>
                                        {liveCalc.stripePct}% + {fmt.format(liveCalc.stripeFixed)}
                                    </strong>{" "}
                                    {t("stripeAccountsPage.infoText2")} <strong>{liveCalc.appFeePct}%</strong>{" "}
                                    {t("stripeAccountsPage.infoText3")}
                                </p>
                                <p style={ui.infoP}>
                                    {t("stripeAccountsPage.infoExample", {
                                        amount: fmt.format(liveCalc.amountPaid),
                                        net: fmt.format(liveCalc.leagueNet),
                                    })}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-lg-9 col-12 mb-4">
                        <div style={ui.wrap}>
                            <div style={ui.body}>
                                <PaginatedFlatTable
                                    title={t("stripeAccountsPage.tableTitle")}
                                    subtitle={t("stripeAccountsPage.tableSubtitle")}
                                    page={page}
                                    onAdd={() => setShowModal(true)}
                                    pages={totalPages}
                                    onPageChange={(p) => setPage(p)}
                                    items={accounts}
                                    loading={loading}
                                    headers={[
                                        t("stripeAccountsPage.tableHeaders.name"),
                                        t("stripeAccountsPage.tableHeaders.email"),
                                        t("stripeAccountsPage.tableHeaders.country"),
                                        t("stripeAccountsPage.tableHeaders.details"),
                                        t("stripeAccountsPage.tableHeaders.last4"),
                                        t("stripeAccountsPage.tableHeaders.status"),
                                        t("stripeAccountsPage.tableHeaders.actions"),
                                    ]}
                                    renderRow={(acc, index) => (
                                        <tr key={acc.id || index}>
                                            <td style={{ fontWeight: 900, color: "#14151a" }}>
                                                {acc.name || t("stripeAccountsPage.noValue")}
                                            </td>
                                            <td style={{ color: "rgba(20,21,26,0.70)", fontWeight: 800 }}>
                                                {acc.user?.email || t("stripeAccountsPage.noValue")}
                                            </td>
                                            <td>{acc.country || t("stripeAccountsPage.noValue")}</td>
                                            <td>
                                                {acc.detailsSubmitted
                                                    ? t("stripeAccountsPage.detailsSubmittedYes")
                                                    : t("stripeAccountsPage.detailsSubmittedNo")}
                                            </td>
                                            <td>{acc.bankLast4 ? `...${acc.bankLast4}` : t("stripeAccountsPage.noValue")}</td>
                                            <td>
                                                {acc.payoutsEnabled ? (
                                                    <span style={{ ...ui.chip, ...ui.chipSuccess }}>
                                                        <i className="fas fa-check-circle" />
                                                        {t("stripeAccountsPage.verified")}
                                                    </span>
                                                ) : (
                                                    <span style={{ ...ui.chip, ...ui.chipWarn }}>
                                                        <i className="fas fa-exclamation-triangle" />
                                                        {t("stripeAccountsPage.needsVerification")}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="text-right">
                                                {!acc.payoutsEnabled && (
                                                    <button
                                                        type="button"
                                                        style={ui.verifyBtn}
                                                        onClick={() => handleCompleteVerification(acc.id)}
                                                        title={t("stripeAccountsPage.completeVerificationTitle")}
                                                    >
                                                        <i className="fas fa-link" />
                                                        {t("stripeAccountsPage.completeVerification")}
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                />
                            </div>
                        </div>

                        {showModal && (
                            <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={ui.modalBackdrop}>
                                <div className="modal-dialog modal-lg modal-dialog-scrollable" role="document" style={ui.modalDialog}>
                                    <div className="modal-content" style={ui.modalContent}>
                                        <div style={ui.modalHeader}>
                                            <h5 style={ui.modalTitle}>{t("stripeAccountsPage.createTitle")}</h5>
                                            <button
                                                type="button"
                                                style={ui.closeBtn}
                                                onClick={() => setShowModal(false)}
                                                aria-label={t("stripeAccountsPage.close")}
                                                title={t("stripeAccountsPage.close")}
                                            >
                                                <i className="fas fa-times" />
                                            </button>
                                        </div>

                                        <div className="modal-body" style={ui.modalBody}>
                                            <CreateStripeAccountModal
                                                onCreated={() => {
                                                    refetch();
                                                    setShowModal(false);
                                                }}
                                                onClose={() => setShowModal(false)}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="col-lg-3 col-12 mb-4">
                        <FeeCalculator
                            defaultCurrency={liveCalc.currency}
                            defaultAmount={liveCalc.amountPaid}
                            defaultAppFeePct={liveCalc.appFeePct}
                            defaultStripePct={liveCalc.stripePct}
                            defaultStripeFixed={liveCalc.stripeFixed}
                            onChange={setLiveCalc}
                            colors={colors}
                        />
                    </div>
                </div>

                <style>{`
          #${scopeId} button:focus,
          #${scopeId} a:focus,
          #${scopeId} .btn:focus {
            outline: none;
            box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18);
          }

          #${scopeId} table thead th {
            background: rgba(0,0,0,0.02);
            border-bottom: 1px solid rgba(0,0,0,0.06);
            color: rgba(20,21,26,0.72);
            font-size: 12.5px;
            font-weight: 900;
            text-transform: uppercase;
            letter-spacing: 0.02em;
          }
          #${scopeId} table tbody td {
            border-top: 1px solid rgba(0,0,0,0.06);
            vertical-align: middle;
            color: rgba(20,21,26,0.86);
            font-weight: 700;
            font-size: 13.5px;
          }
          #${scopeId} table tbody tr:hover {
            background: rgba(var(--kbs-primary-rgb, 0,173,255), 0.10);
          }

          #${scopeId} i.fas,
          #${scopeId} i.far,
          #${scopeId} i.fal,
          #${scopeId} i.fa,
          #${scopeId} i[class^="fa-"],
          #${scopeId} i[class*=" fa-"] {
            font-family: "Font Awesome 5 Free" !important;
            font-weight: 900;
          }
          #${scopeId} i.fab {
            font-family: "Font Awesome 5 Brands" !important;
            font-weight: 400;
          }
        `}</style>
            </section>
        </div>
    );
}
